import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import i18next from 'i18next';

import { Table, TBody, TElement, THead, TRow } from '@adeccoux/tag-ds';
import { ClientOrganizationModel } from '@models/clientOrganization.model';
import { OnboardingFlowUsersModel } from '@models/onboarding-steps';
import { CheckUser } from '@models/user.model';
import { Grid, Typography } from '@mui/material';

import { SortingButton } from '../AdminDashboardTable/SortingButton';
import AdminPagination from '../AdminPagination';

import styles from './SoftDeletedApplicationsTable.module.css';
interface SoftDeletedCandidateTableProps {
  data: any[];
  allOrganizations?: ClientOrganizationModel[];
  allJournies: any[];
  dateFormat: string;
  selectedUser: CheckUser | null;
  handleSelectUser: (user: CheckUser) => void;
  handleRestoreUser?: () => void;
  pagination: {
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    total: number;
    rowsPerPage: number;
    setRowsPerPage: Dispatch<SetStateAction<number>>;
  };
  order: string;
  orderBy: string;
  setOrder: (order: string) => void;
  setOrderBy: (orderBy: string) => void;
  canRestoreCandidate: boolean;
}

export type SoftDeletedCandidateTableSelectionType =
  | SelectionType.pageEntries
  | SelectionType.singleEntry
  | SelectionType.totalEntries
  | '';

export enum softDeletedCandidateTableActionType {
  reject = 'reject',
  delete = 'delete',
  approve = 'approve',
}

export enum SelectionType {
  pageEntries = 'pageEntries',
  singleEntry = 'singleEntry',
  totalEntries = 'totalEntries',
}

export enum DashboardColumnHeader {
  headerName = 'headerName',
  headerLastName = 'headerLastName',
  headerClient = 'headerClient',
  headerJourney = 'headerJourney',
  headerBranch = 'headerBranch',
  headerApplication = 'headerApplication',
  headerSoftDeleteDate = 'headerSoftDeleteDate',
  headerStatusBeforeDeletion = 'headerStatusBeforeDeletion',
  headerDeletionReason = 'headerDeletionReason',
}

export interface ColumnDefinitionInterfaceItem {
  field: string;
  headerName: DashboardColumnHeader;
  sorting: boolean;
  element: JSX.Element | null;
}

export type ColumnDefinition = {
  [key in keyof typeof DashboardColumnHeader]: ColumnDefinitionInterfaceItem | null;
};

const SoftDeletedApplicationsTable: React.FC<SoftDeletedCandidateTableProps> = ({
  data,
  allOrganizations,
  allJournies,
  dateFormat,
  handleSelectUser,
  handleRestoreUser,
  selectedUser,
  pagination,
  order,
  orderBy,
  setOrder,
  setOrderBy,
  canRestoreCandidate,
}) => {
  const { t } = useTranslation(['recruiter', 'stepper']);

  const useSeparateFirstAndLastName = allJournies?.some((j) => j.additionalConfigurations?.useSeparateFirstAndLastName);
  const showOnboardingStatus = allJournies?.some((j) => j.additionalConfigurations?.showOnboardingStatus);
  const hideApplicationColumn = allJournies?.every((j) => !!j.additionalConfigurations?.hideApplicationColumn);
  const getTranslation = (translationPath: string, key: string) => {
    const i18nString = `${translationPath}.${key}`;
    return i18next.exists(i18nString) ? t(i18nString) : key;
  };
  const buildColumns = (onboardingFlowUsersModel?: OnboardingFlowUsersModel): ColumnDefinitionInterfaceItem[] => {
    const hasRequisitionId = !!onboardingFlowUsersModel?.requisitionId;
    const columns: ColumnDefinition = {
      headerName: {
        field: `"UserProfile"."firstName"`,
        headerName: DashboardColumnHeader.headerName,
        sorting: true,
        element: onboardingFlowUsersModel ? (
          <TElement>{onboardingFlowUsersModel.userProfile?.firstName}</TElement>
        ) : null,
      },
      headerLastName: useSeparateFirstAndLastName
        ? {
            field: `"UserProfile"."lastName"`,
            headerName: DashboardColumnHeader.headerLastName,
            sorting: true,
            element: onboardingFlowUsersModel ? (
              <TElement>{onboardingFlowUsersModel.userProfile?.lastName}</TElement>
            ) : null,
          }
        : null,
      headerClient: {
        field: ``,
        headerName: DashboardColumnHeader.headerClient,
        sorting: true,
        element: onboardingFlowUsersModel ? (
          <TElement>
            {getTranslation(`recruiter:CLIENT_NAME`, getClientName(onboardingFlowUsersModel.clientOrganizationId))}
          </TElement>
        ) : null,
      },
      headerJourney: {
        field: `"Onboarding"."applicationId"`,
        headerName: DashboardColumnHeader.headerJourney,
        sorting: true,
        element: onboardingFlowUsersModel ? (
          <TElement>
            {getTranslation(
              `recruiter:CLIENT_NAME`,
              getJourneyName(onboardingFlowUsersModel.configurationId, onboardingFlowUsersModel.clientOrganizationId),
            )}
          </TElement>
        ) : null,
      },
      headerBranch: {
        field: `"Branch"."name"`,
        headerName: DashboardColumnHeader.headerBranch,
        sorting: true,
        element: onboardingFlowUsersModel ? <TElement>{onboardingFlowUsersModel.branch?.sid}</TElement> : null,
      },
      headerApplication: hideApplicationColumn
        ? null
        : {
            field: `"Onboarding"."applicationId"`,
            headerName: DashboardColumnHeader.headerApplication,
            sorting: true,
            element: onboardingFlowUsersModel ? (
              <TElement>
                {hasRequisitionId
                  ? `${onboardingFlowUsersModel.applicationId} - ${onboardingFlowUsersModel.requisitionId}`
                  : onboardingFlowUsersModel.applicationId}
              </TElement>
            ) : null,
          },
      headerSoftDeleteDate: {
        field: `"Onboarding"."deletedAt"`,
        headerName: DashboardColumnHeader.headerSoftDeleteDate,
        sorting: true,
        element: onboardingFlowUsersModel ? (
          <TElement>
            <Grid container>
              <Grid item xs={12}>
                <span>
                  {onboardingFlowUsersModel?.deletedAt && formatTableDate(onboardingFlowUsersModel?.deletedAt)}
                </span>
              </Grid>
              <Grid item xs={12}>
                <Typography className="feature-dashboard__table-small-text">
                  {onboardingFlowUsersModel?.deletedAt && formatTableTime(onboardingFlowUsersModel?.deletedAt)}
                </Typography>
              </Grid>
            </Grid>
          </TElement>
        ) : null,
      },
      headerStatusBeforeDeletion: showOnboardingStatus
        ? {
            field: `"Onboarding"."statusBeforeDeletion"`,
            headerName: DashboardColumnHeader.headerStatusBeforeDeletion,
            sorting: true,
            element: onboardingFlowUsersModel ? (
              <TElement>
                {getTranslation(
                  `recruiter:GENERAL.CANDIDATE_DETAILS`,
                  onboardingFlowUsersModel?.statusBeforeDeletion ?? '',
                )}
              </TElement>
            ) : null,
          }
        : null,
      headerDeletionReason: {
        field: `"Onboarding"."toBeDeletedReason"`,
        headerName: DashboardColumnHeader.headerDeletionReason,
        sorting: true,
        element: (
          <TElement>
            {`${getTranslation(
              `recruiter:GENERAL.CANDIDATE_DELETION`,
              onboardingFlowUsersModel?.userProfile?.user?.toBeDeletedReason ?? '',
            )} ${
              onboardingFlowUsersModel?.userProfile?.user?.toBeDeletedReason === 'deletion' &&
              onboardingFlowUsersModel?.actor === 'RECRUITER'
                ? `${onboardingFlowUsersModel?.actorEmail ? `[${onboardingFlowUsersModel?.actorEmail}]` : ''}`
                : ''
            }`}
          </TElement>
        ),
      },
    };

    return Object.values(columns).filter((item) => !!item) as ColumnDefinitionInterfaceItem[];
  };

  const getClientName = (id: string) => {
    const client = allOrganizations?.find((c) => c.id === id);
    return client?.displayName ?? '';
  };

  const getJourneyName = (id: string, clientOrganizationId: string) => {
    const journey = allJournies?.find((j: any) => j.id === id && j.clientOrganizationId === clientOrganizationId);

    return journey?.recruiterName ? journey?.recruiterName : '';
  };

  const defaultDateFormat = 'yyyy-MM-dd';
  const formatTableTime = (date: Date) => format(new Date(date), 'HH:mm:ss');
  const formatTableDate = (date: Date) => format(new Date(date), dateFormat || defaultDateFormat);
  const generateSelectionText = (type: string) => {
    const res: any = {
      pageEntries: t('GENERAL.GENERIC.pageEntries', { pageEntries: data.length }),
      singleEntry: t('GENERAL.GENERIC.singleEntry', { selectedUsers: selectedUser ? 1 : 0 }),
      totalEntries: t('GENERAL.GENERIC.totalEntries', { totalEntries: pagination.total }),
    };

    return res[type];
  };
  return (
    <div className="tag-ds App visible-scroll">
      {selectedUser && (
        <div className="data-table-wrapper data-table-action-bar">
          <div className="data-table-action-bar">
            <span className="data-table-items-selected">{generateSelectionText('singleEntry')}</span>

            <div className="data-table-action-items" style={{ gap: 0 }}>
              <button className="admin-table-buttons small" onClick={handleRestoreUser}>
                {t('CANDIDATES_OVERVIEW.restore')} <span className="material-icons left">do_not_disturb_on</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <Table className={styles.softDeletedCandidatesTable}>
        <THead>
          <TRow>
            {canRestoreCandidate && <TElement />}
            {buildColumns().map(({ sorting, field, headerName }, index) => {
              return (
                <TElement key={field + index} style={{ position: 'relative' }}>
                  {sorting && (
                    <SortingButton
                      onClick={() => {
                        setOrder(orderBy === field && order === 'asc' ? 'desc' : 'asc');
                        setOrderBy(field);
                      }}
                      direction={orderBy === field ? order : 'asc'}
                      active={orderBy === field}
                    />
                  )}
                  {t(`recruiter:GENERAL.IMPORT.${headerName}`)}
                </TElement>
              );
            })}
          </TRow>
        </THead>
        <TBody>
          {data.length > 0 &&
            data.map((x, i) => {
              const isChecked = selectedUser
                ? selectedUser.userId === x.userId &&
                  selectedUser.configurationId === x.configurationId &&
                  selectedUser.applicationId === x.applicationId
                : false;
              return (
                <TRow key={i}>
                  {canRestoreCandidate && (
                    <TElement>
                      <div className="checkbox-container">
                        <input
                          id="checkbox"
                          onChange={() => {
                            handleSelectUser(x);
                          }}
                          type="checkbox"
                          checked={isChecked}
                        />
                      </div>
                    </TElement>
                  )}
                  {buildColumns(x).map(({ element }, index) => (
                    <React.Fragment key={index}>{element}</React.Fragment>
                  ))}
                </TRow>
              );
            })}
        </TBody>
      </Table>
      {data.length > 0 && (
        <AdminPagination
          rowsPerPage={pagination.rowsPerPage}
          setRowsPerPage={pagination.setRowsPerPage}
          total={pagination.total}
          currentPage={pagination.page}
          setPage={pagination.setPage}
          isRowsPerPageVisible={true}
        />
      )}
    </div>
  );
};

export default SoftDeletedApplicationsTable;
